import React, { useEffect, useMemo, useRef } from "react";
import { Line } from "react-chartjs-2";
import { formatLocalAxisTime, formatLocalDateTime } from "../../../utils/dateTime";
import { chartTimePoint, liveTimeExtent, liveZoomSource, planLiveAxis, rememberLiveEdge } from "../../../utils/liveHistory";
import { gapSegment } from "../../../utils/statusGaps";

const COMBINED_CHART_ID = "combined";
const EMPTY_ROWS = [];
const MAX_RENDER_POINTS_PER_SERIES = 1000;
const contrastColor = (theme) => (theme === "dark" ? "#F8FAFC" : "#0F172A");
const defaultGridHex = (theme) => (theme === "dark" ? "#94A3B8" : "#64748B");
const defaultGridOpacity = (theme) => (theme === "dark" ? 0.4 : 0.55);
const hexToRgba = (hex, opacity) => {
    const raw = String(hex || "").replace("#", "");
    const value = Number.parseInt(raw, 16);
    if (!Number.isFinite(value) || raw.length !== 6) return `rgba(100, 116, 139, ${opacity})`;
    return `rgba(${(value >> 16) & 255}, ${(value >> 8) & 255}, ${value & 255}, ${opacity})`;
};

const readScaleRange = (scale) => {
    if (!scale) {
        return null;
    }
    const min = Number.isFinite(scale.options?.min) ? scale.options.min : scale.min;
    const max = Number.isFinite(scale.options?.max) ? scale.options.max : scale.max;
    if (!Number.isFinite(min) || !Number.isFinite(max)) {
        return null;
    }
    return { min, max };
};

const applyChartXRange = (chart, min, max, { releaseZoom = false } = {}) => {
    const xScale = chart?.options?.scales?.x;
    if (!xScale || !Number.isFinite(min) || !Number.isFinite(max) || !(max > min)) {
        return false;
    }
    chart.stop?.();
    xScale.min = min;
    xScale.max = max;
    chart.update("none");
    if (releaseZoom && typeof chart.resetZoom === "function") {
        chart.resetZoom("none");
    }
    return true;
};

const applyLiveTimeViewport = (chart, rows, { followLatest, viewport, previousLatest }) => {
    const extent = liveTimeExtent(rows);
    const plan = planLiveAxis({ followLatest, viewport, previousLatest, extent });
    if (!plan || plan.mode === "none") {
        return null;
    }
    applyChartXRange(chart, plan.min, plan.max, { releaseZoom: plan.mode === "fit" });
    return { ...plan, latest: extent.latest };
};

const syncLiveChartViewport = (chart, rows, {
    followLatest,
    preserveViewport,
    viewportRef,
    liveEdgeRef,
    onResumeFollow,
}) => {
    const result = applyLiveTimeViewport(chart, rows, {
        followLatest,
        viewport: followLatest ? null : (preserveViewport ? viewportRef.current : null),
        previousLatest: liveEdgeRef.current,
    });
    if (!result) {
        return;
    }
    liveEdgeRef.current = result.latest;
    viewportRef.current = result.mode === "fit" ? null : { min: result.min, max: result.max };
    if (result.resumeFollow) {
        onResumeFollow?.();
    }
};

const timeScale = (chrome) => ({
    type: "time",
    bounds: "data",
    time: {
        tooltipFormat: "yyyy-MM-dd HH:mm:ss.SSS",
        displayFormats: {
            millisecond: "HH:mm:ss.SSS",
            second: "HH:mm:ss",
            minute: "HH:mm",
            hour: "HH:mm",
            day: "dd MMM",
        },
    },
    title: { display: true, text: "Time", color: chrome.label },
    ticks: {
        color: chrome.tick,
        maxRotation: 0,
        autoSkip: true,
        maxTicksLimit: 8,
        callback(value) {
            return formatLocalAxisTime(value);
        },
    },
    grid: { color: chrome.grid, lineWidth: 1 },
    border: { color: chrome.frame },
});

const axisChrome = (theme, appearance) => {
    const gridHex = appearance.gridColor || defaultGridHex(theme);
    const gridOpacity = appearance.gridOpacity == null ? defaultGridOpacity(theme) : appearance.gridOpacity;
    return {
        tick: theme === "dark" ? "#CBD5E1" : "#475569",
        label: theme === "dark" ? "#F1F5F9" : "#0F172A",
        grid: hexToRgba(gridHex, gridOpacity),
        frame: gridHex,
    };
};

const isSelectedPoint = (selection, seriesId, index) => (
    Boolean(selection) && selection.seriesId === seriesId && selection.index === index
);

const sourceIndexForPoint = (context) => (
    Number.isInteger(context?.raw?.sourceIndex) ? context.raw.sourceIndex : context.dataIndex
);

const sampledRows = (rows, selectedIndex = null, limit = MAX_RENDER_POINTS_PER_SERIES) => {
    if (rows.length <= limit) {
        return rows.map((row, sourceIndex) => ({ row, sourceIndex }));
    }

    const selected = new Set([0, rows.length - 1]);
    if (Number.isInteger(selectedIndex) && selectedIndex >= 0 && selectedIndex < rows.length) {
        selected.add(selectedIndex);
    }
    const bucketCount = Math.max(1, Math.floor((limit - selected.size) / 2));
    const interiorLength = Math.max(0, rows.length - 2);
    const bucketSize = interiorLength / bucketCount;

    for (let bucket = 0; bucket < bucketCount; bucket += 1) {
        const start = 1 + Math.floor(bucket * bucketSize);
        const end = Math.min(rows.length - 1, 1 + Math.floor((bucket + 1) * bucketSize));
        let minIndex = start;
        let maxIndex = start;
        let minValue = Number(rows[start]?.value);
        let maxValue = minValue;
        for (let index = start + 1; index < end; index += 1) {
            const value = Number(rows[index]?.value);
            if (value < minValue) {
                minValue = value;
                minIndex = index;
            }
            if (value > maxValue) {
                maxValue = value;
                maxIndex = index;
            }
        }
        selected.add(minIndex);
        selected.add(maxIndex);
    }

    return [...selected]
        .sort((left, right) => left - right)
        .map((sourceIndex) => ({ row: rows[sourceIndex], sourceIndex }));
};

const pointVisuals = (color, seriesId, appearance, theme, selectionRef) => ({
    borderColor: color,
    backgroundColor: color,
    borderWidth: appearance.lineWidth,
    tension: 0,
    fill: false,
    spanGaps: false,
    borderCapStyle: "round",
    borderJoinStyle: "round",
    pointBackgroundColor: color,
    pointHoverBackgroundColor: color,
    pointHoverBorderColor: contrastColor(theme),
    pointHoverBorderWidth: 2,
    pointHitRadius: 12,
    pointBorderColor(context) {
        return isSelectedPoint(selectionRef.current, seriesId, sourceIndexForPoint(context))
            ? contrastColor(theme)
            : color;
    },
    pointBorderWidth(context) {
        return isSelectedPoint(selectionRef.current, seriesId, sourceIndexForPoint(context)) ? 2.5 : 0;
    },
    pointRadius(context) {
        if (isSelectedPoint(selectionRef.current, seriesId, sourceIndexForPoint(context))) {
            return Math.max(Number(appearance.pointSize) + 4, 7);
        }
        const denseSeries = (context.dataset?.data?.length || 0) > 1500;
        return appearance.showPoints && !denseSeries ? appearance.pointSize : 0;
    },
    pointHoverRadius(context) {
        if (isSelectedPoint(selectionRef.current, seriesId, sourceIndexForPoint(context))) {
            return Math.max(Number(appearance.pointSize) + 4, 7);
        }
        return Math.max(appearance.showPoints ? appearance.pointSize : 4, 4);
    },
});

const zoomPluginOptions = (theme, onChange, minRange) => ({
    pan: {
        enabled: true,
        mode: "x",
        modifierKey: "shift",
        threshold: 8,
        onPanComplete: ({ chart: pannedChart }) => onChange(pannedChart, "user"),
    },
    limits: {
        x: { min: "original", max: "original", minRange },
    },
    zoom: {
        mode: "x",
        wheel: {
            enabled: true,
            speed: 0.08,
        },
        pinch: {
            enabled: true,
        },
        drag: {
            enabled: true,
            threshold: 8,
            drawTime: "afterDatasetsDraw",
            backgroundColor: theme === "dark" ? "rgba(56, 189, 248, 0.18)" : "rgba(37, 99, 235, 0.16)",
            borderColor: theme === "dark" ? "#38BDF8" : "#2563EB",
            borderWidth: 1,
        },
        onZoom: ({ chart: zoomedChart, trigger }) => onChange(
            zoomedChart,
            trigger && trigger !== "api" ? "user" : "api"
        ),
        onZoomComplete: ({ chart: zoomedChart }) => onChange(zoomedChart, "api"),
    },
});

const revealChartPoint = (chart, index) => {
    const scale = chart.scales?.x;
    if (!scale || typeof chart.zoomScale !== "function") {
        return;
    }
    if (index >= scale.min && index <= scale.max) {
        return;
    }
    const bounds = chart.getInitialScaleBounds?.().x || {};
    const originalMin = Number.isFinite(bounds.min) ? bounds.min : 0;
    const originalMax = Number.isFinite(bounds.max) ? bounds.max : Math.max(0, (chart.data.labels?.length || 1) - 1);
    const span = Math.max(scale.max - scale.min, 8);
    let min = index - span / 2;
    let max = index + span / 2;
    if (min < originalMin) {
        max += originalMin - min;
        min = originalMin;
    }
    if (max > originalMax) {
        min -= max - originalMax;
        max = originalMax;
    }
    chart.zoomScale("x", {
        min: Math.max(originalMin, min),
        max: Math.min(originalMax, max),
    }, "none");
};

const revealTimePoint = (chart, xValue) => {
    const scale = chart.scales?.x;
    if (!scale || !Number.isFinite(xValue) || typeof chart.zoomScale !== "function") {
        return;
    }
    if (xValue >= scale.min && xValue <= scale.max) {
        return;
    }
    const bounds = chart.getInitialScaleBounds?.().x || {};
    const originalMin = Number.isFinite(bounds.min) ? bounds.min : scale.min;
    const originalMax = Number.isFinite(bounds.max) ? bounds.max : scale.max;
    const span = Math.max(scale.max - scale.min, 60 * 1000);
    let min = xValue - span / 2;
    let max = xValue + span / 2;
    if (min < originalMin) {
        max += originalMin - min;
        min = originalMin;
    }
    if (max > originalMax) {
        min -= max - originalMax;
        max = originalMax;
    }
    chart.zoomScale("x", {
        min: Math.max(originalMin, min),
        max: Math.min(originalMax, max),
    }, "none");
};

const TagTrendCanvas = ({
    chart,
    color,
    label,
    theme,
    appearance,
    selectedIndex,
    zoomed,
    axisMode,
    preserveViewport,
    followLatest,
    followEpoch,
    onSelect,
    onZoomChange,
    onManualZoom,
    onResumeFollow,
    onResetZoom,
    cursorActive,
    onClearCursor,
}) => {
    const chartRef = useRef(null);
    const selectionRef = useRef(null);
    const viewportRef = useRef(null);
    const liveEdgeRef = useRef(null);
    selectionRef.current = selectedIndex == null ? null : { seriesId: chart.id, index: selectedIndex };
    const rows = Array.isArray(chart.tableData) ? chart.tableData : EMPTY_ROWS;
    const hidden = chart.visible === false;
    const useTimeAxis = axisMode === "time";
    const renderedRows = useMemo(
        () => sampledRows(rows, selectedIndex),
        [rows, selectedIndex]
    );

    const data = useMemo(() => {
        if (!chart.chartData) {
            return null;
        }
        const visuals = {
            ...pointVisuals(color, chart.id, appearance, theme, selectionRef),
            seriesId: chart.id,
            label: label || chart.tagName || `Tag ${chart.tagId}`,
            hidden,
        };
        if (useTimeAxis) {
            return {
                datasets: [
                    {
                        ...visuals,
                        parsing: false,
                        segment: gapSegment(chart.statusEvents),
                        spanGaps: false,
                        data: renderedRows.map(({ row, sourceIndex }) => ({
                            ...(chartTimePoint(row) || { x: null, y: null }),
                            sourceIndex,
                        })),
                    },
                ],
            };
        }
        return {
            ...chart.chartData,
            datasets: chart.chartData.datasets.map((dataset) => ({
                ...dataset,
                ...visuals,
            })),
        };
    }, [appearance, chart.chartData, chart.id, chart.statusEvents, chart.tagId, chart.tagName, color, hidden, label, renderedRows, theme, useTimeAxis]);

    const options = useMemo(() => {
        const chrome = axisChrome(theme, appearance);
        const tagName = label || chart.tagName || `Tag ${chart.tagId}`;
        const reportZoom = (instance, source) => {
            const range = readScaleRange(instance?.scales?.x);
            const extent = liveTimeExtent(rows);
            if (range) {
                viewportRef.current = range;
            }
            if (source === "user") {
                liveEdgeRef.current = rememberLiveEdge(liveEdgeRef.current, range?.max, extent);
            }
            onZoomChange(
                chart.id,
                instance,
                liveZoomSource(source, range?.min, range?.max, extent)
            );
        };

        return {
            responsive: true,
            maintainAspectRatio: false,
            animation: false,
            normalized: true,
            interaction: {
                mode: "nearest",
                intersect: true,
                axis: "xy",
            },
            onClick: (_event, elements) => {
                const hit = elements?.[0];
                if (!hit) {
                    return;
                }
                const sourceIndex = data?.datasets?.[hit.datasetIndex]?.data?.[hit.index]?.sourceIndex;
                onSelect(chart.id, Number.isInteger(sourceIndex) ? sourceIndex : hit.index);
            },
            plugins: {
                decimation: {
                    enabled: false,
                },
                legend: {
                    labels: {
                        color: chrome.label,
                        usePointStyle: true,
                        font: {
                            family: '"Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                        },
                    },
                },
                tooltip: {
                    callbacks: {
                        title(items) {
                            const item = items[0];
                            const index = Number.isInteger(item?.raw?.sourceIndex)
                                ? item.raw.sourceIndex
                                : item?.dataIndex;
                            const row = rows[index];
                            return row ? formatLocalDateTime(row.timestamp) : "";
                        },
                        label(item) {
                            const index = Number.isInteger(item?.raw?.sourceIndex)
                                ? item.raw.sourceIndex
                                : item.dataIndex;
                            const row = rows[index];
                            const value = row ? row.value : item.parsed.y;
                            return `${tagName}: ${value}`;
                        },
                    },
                },
                zoom: zoomPluginOptions(theme, reportZoom, useTimeAxis ? 1 : 2),
            },
            scales: {
                x: useTimeAxis ? timeScale(chrome) : {
                    title: { display: true, text: "Time", color: chrome.label },
                    ticks: { color: chrome.tick, maxRotation: 0, autoSkip: true, maxTicksLimit: 8 },
                    grid: { color: chrome.grid, lineWidth: 1 },
                    border: { color: chrome.frame },
                },
                y: {
                    title: { display: true, text: "Value", color: chrome.label },
                    ticks: { color: chrome.tick },
                    grid: { color: chrome.grid, lineWidth: 1 },
                    border: { color: chrome.frame },
                },
            },
        };
    }, [appearance, chart.id, chart.tagId, chart.tagName, data, label, onSelect, onZoomChange, rows, theme, useTimeAxis]);

    useEffect(() => {
        viewportRef.current = null;
        const instance = chartRef.current;
        if (instance && typeof instance.resetZoom === "function") {
            instance.resetZoom("none");
        }
    }, [useTimeAxis]);

    useEffect(() => {
        const instance = chartRef.current;
        if (!instance) {
            return;
        }
        if (useTimeAxis && (followLatest || preserveViewport)) {
            syncLiveChartViewport(instance, rows, {
                followLatest,
                preserveViewport,
                viewportRef,
                liveEdgeRef,
                onResumeFollow,
            });
            return;
        }
        if (typeof instance.resetZoom !== "function") {
            return;
        }
        viewportRef.current = null;
        liveEdgeRef.current = null;
        instance.resetZoom("none");
    }, [chart.chartData, followEpoch, followLatest, onResumeFollow, preserveViewport, rows, useTimeAxis]);

    useEffect(() => {
        const instance = chartRef.current;
        if (!instance) {
            return;
        }
        if (selectedIndex == null) {
            instance.$selectionGuide = null;
            instance.setActiveElements?.([]);
            instance.update("none");
            return;
        }
        let displayIndex = selectedIndex;
        if (useTimeAxis) {
            const row = rows[selectedIndex];
            revealTimePoint(instance, chartTimePoint(row)?.x ?? NaN);
            displayIndex = data?.datasets?.[0]?.data?.findIndex(
                (point) => point.sourceIndex === selectedIndex
            );
        } else {
            revealChartPoint(instance, selectedIndex);
        }
        if (!Number.isInteger(displayIndex) || displayIndex < 0) {
            return;
        }
        instance.$selectionGuide = {
            index: displayIndex,
            datasetIndex: 0,
            color,
            contrast: contrastColor(theme),
        };
        instance.setActiveElements?.([{ datasetIndex: 0, index: displayIndex }]);
        const point = instance.getDatasetMeta(0)?.data?.[displayIndex];
        if (point && instance.tooltip) {
            instance.tooltip.setActiveElements(
                [{ datasetIndex: 0, index: displayIndex }],
                { x: point.x, y: point.y }
            );
        }
        instance.update("none");
        onZoomChange(chart.id, instance, "api");
    }, [chart.id, color, data, onZoomChange, rows, selectedIndex, theme, useTimeAxis]);

    if (!data) {
        return null;
    }

    const selectedRow = selectedIndex == null ? null : rows[selectedIndex] || null;

    return (
        <article className="graph-page__card graph-page__chart-card">
            <div className="graph-page__card-head graph-page__card-head--split">
                <div>
                    <h2>{label || chart.tagName || `Tag ${chart.tagId}`}</h2>
                    <p>{rows.length} rows</p>
                </div>
                <span className="graph-page__swatch" style={{ background: color }} aria-hidden="true" />
            </div>
            <div className="graph-page__zoom">
                <button type="button" className="graph-page__button" onClick={() => { onManualZoom?.(); chartRef.current?.zoom?.({ x: 1.2, y: 1 }); }}>
                    Zoom In
                </button>
                <button type="button" className="graph-page__button" onClick={() => { onManualZoom?.(); chartRef.current?.zoom?.({ x: 0.8, y: 1 }); }}>
                    Zoom Out
                </button>
                <button type="button" className="graph-page__button" onClick={() => onResetZoom?.(chartRef.current)} disabled={!zoomed}>
                    Reset Zoom
                </button>
                <button type="button" className="graph-page__button" onClick={onClearCursor} disabled={!cursorActive}>
                    Clear Cursor
                </button>
                {zoomed && <span className="graph-page__zoom-state">Zoomed range</span>}
            </div>
            <p className="graph-page__hint">Wheel zooms the time axis. Drag a range to zoom. Shift-drag pans across the loaded samples.</p>
            {selectedRow && (
                <p className="graph-page__selection" role="status">
                    Selected {label || chart.tagName || `Tag ${chart.tagId}`} · {formatLocalDateTime(selectedRow.timestamp)} · {selectedRow.value}
                </p>
            )}
            {hidden && <p className="graph-page__hint">This series is hidden.</p>}
            <div className="graph-page__plot">
                <Line ref={chartRef} datasetIdKey="seriesId" data={data} options={options} />
            </div>
        </article>
    );
};

const MemoizedTagTrendCanvas = React.memo(TagTrendCanvas);

const TagTrend = (props) => {
    const viewportRef = useRef(null);
    const cachedPropsRef = useRef(props);
    const [renderActive, setRenderActive] = React.useState(true);

    if (renderActive) {
        cachedPropsRef.current = props;
    }

    useEffect(() => {
        const element = viewportRef.current;
        if (!element || typeof IntersectionObserver === "undefined") {
            return undefined;
        }
        const observer = new IntersectionObserver(
            ([entry]) => setRenderActive(entry.isIntersecting),
            { root: null, rootMargin: "600px 0px", threshold: 0 }
        );
        observer.observe(element);
        return () => observer.disconnect();
    }, []);

    return (
        <div ref={viewportRef} className="graph-page__chart-viewport">
            <MemoizedTagTrendCanvas {...cachedPropsRef.current} />
        </div>
    );
};

const CombinedTrend = ({
    series,
    theme,
    appearance,
    selection,
    zoomed,
    preserveViewport,
    followLatest,
    followEpoch,
    onSelect,
    onZoomChange,
    onToggleVisible,
    onManualZoom,
    onResumeFollow,
    onResetZoom,
    cursorActive,
    onClearCursor,
}) => {
    const chartRef = useRef(null);
    const selectionRef = useRef(selection);
    const viewportRef = useRef(null);
    const liveEdgeRef = useRef(null);
    const dataIdentity = useRef({ next: 1, map: new WeakMap() });
    selectionRef.current = selection;
    const seriesKey = series.map((item) => {
        const chartData = item.chart.chartData;
        if (!chartData) {
            return `${item.chart.id}:0`;
        }
        let identity = dataIdentity.current.map.get(chartData);
        if (!identity) {
            identity = dataIdentity.current.next;
            dataIdentity.current.next += 1;
            dataIdentity.current.map.set(chartData, identity);
        }
        return `${item.chart.id}:${identity}`;
    }).join("|");

    const data = useMemo(() => ({
        datasets: series.map((item) => {
            const rows = Array.isArray(item.chart.tableData) ? item.chart.tableData : EMPTY_ROWS;
            const selectedIndex = selection?.seriesId === item.chart.id ? selection.index : null;
            return {
                ...pointVisuals(item.color, item.chart.id, appearance, theme, selectionRef),
                seriesId: item.chart.id,
                label: item.label,
                hidden: item.chart.visible === false,
                yAxisID: item.chart.yAxis === "y1" ? "y1" : "y",
                parsing: false,
                segment: gapSegment(item.chart.statusEvents),
                spanGaps: false,
                data: sampledRows(rows, selectedIndex).map(({ row, sourceIndex }) => ({
                    ...(chartTimePoint(row) || { x: null, y: null }),
                    sourceIndex,
                })),
            };
        }),
    }), [appearance, selection, series, theme]);

    const options = useMemo(() => {
        const chrome = axisChrome(theme, appearance);
        const showRight = series.some((item) => item.chart.visible !== false && item.chart.yAxis === "y1");
        const showLeft = series.some((item) => item.chart.visible !== false && item.chart.yAxis !== "y1");
        const reportZoom = (instance, source) => {
            const range = readScaleRange(instance?.scales?.x);
            const rows = series.flatMap((item) => (Array.isArray(item.chart.tableData) ? item.chart.tableData : []));
            const extent = liveTimeExtent(rows);
            if (range) {
                viewportRef.current = range;
            }
            if (source === "user") {
                liveEdgeRef.current = rememberLiveEdge(liveEdgeRef.current, range?.max, extent);
            }
            onZoomChange(
                COMBINED_CHART_ID,
                instance,
                liveZoomSource(source, range?.min, range?.max, extent)
            );
        };

        return {
            responsive: true,
            maintainAspectRatio: false,
            animation: false,
            normalized: true,
            interaction: {
                mode: "nearest",
                intersect: true,
                axis: "xy",
            },
            onClick: (_event, elements, clickedChart) => {
                const hit = elements?.[0];
                const dataset = hit ? clickedChart.data.datasets[hit.datasetIndex] : null;
                if (!hit || dataset?.seriesId == null) {
                    return;
                }
                const sourceIndex = dataset.data?.[hit.index]?.sourceIndex;
                onSelect(dataset.seriesId, Number.isInteger(sourceIndex) ? sourceIndex : hit.index);
            },
            plugins: {
                decimation: {
                    enabled: false,
                },
                legend: {
                    labels: {
                        color: chrome.label,
                        usePointStyle: true,
                        font: {
                            family: '"Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                        },
                    },
                    onClick(_event, legendItem) {
                        const dataset = series[legendItem.datasetIndex];
                        if (dataset) {
                            onToggleVisible(dataset.chart.id);
                        }
                    },
                },
                tooltip: {
                    callbacks: {
                        title(items) {
                            const item = items[0];
                            const source = item ? series[item.datasetIndex] : null;
                            const sourceIndex = Number.isInteger(item?.raw?.sourceIndex)
                                ? item.raw.sourceIndex
                                : item?.dataIndex;
                            const row = source?.chart.tableData?.[sourceIndex];
                            return row ? formatLocalDateTime(row.timestamp) : "";
                        },
                        label(item) {
                            const source = series[item.datasetIndex];
                            const sourceIndex = Number.isInteger(item?.raw?.sourceIndex)
                                ? item.raw.sourceIndex
                                : item.dataIndex;
                            const row = source?.chart.tableData?.[sourceIndex];
                            const name = source?.label || "Tag";
                            const value = row ? row.value : item.parsed.y;
                            return `${name}: ${value}`;
                        },
                    },
                },
                zoom: zoomPluginOptions(theme, reportZoom, 1),
            },
            scales: {
                x: timeScale(chrome),
                y: {
                    type: "linear",
                    position: "left",
                    display: showLeft || !showRight,
                    title: { display: true, text: showRight ? "Left value" : "Value", color: chrome.label },
                    ticks: { color: chrome.tick },
                    grid: { color: chrome.grid, lineWidth: 1, drawOnChartArea: showLeft || !showRight },
                    border: { color: chrome.frame },
                },
                y1: {
                    type: "linear",
                    position: "right",
                    display: showRight,
                    title: { display: true, text: "Right value", color: chrome.label },
                    ticks: { color: chrome.tick },
                    grid: { color: chrome.grid, lineWidth: 1, drawOnChartArea: !showLeft && showRight },
                    border: { color: chrome.frame },
                },
            },
        };
    }, [appearance, onSelect, onToggleVisible, onZoomChange, series, theme]);

    useEffect(() => {
        const instance = chartRef.current;
        if (!instance) {
            return;
        }
        const rows = series.flatMap((item) => (Array.isArray(item.chart.tableData) ? item.chart.tableData : []));
        if (followLatest || preserveViewport) {
            syncLiveChartViewport(instance, rows, {
                followLatest,
                preserveViewport,
                viewportRef,
                liveEdgeRef,
                onResumeFollow,
            });
            return;
        }
        if (typeof instance.resetZoom !== "function") {
            return;
        }
        viewportRef.current = null;
        liveEdgeRef.current = null;
        instance.resetZoom("none");
        onZoomChange(COMBINED_CHART_ID, instance, "api");
    }, [followEpoch, followLatest, onResumeFollow, onZoomChange, preserveViewport, series, seriesKey]);

    useEffect(() => {
        const instance = chartRef.current;
        if (!instance) {
            return;
        }
        const datasetIndex = selection
            ? instance.data.datasets.findIndex((dataset) => dataset.seriesId === selection.seriesId)
            : -1;
        const meta = datasetIndex >= 0 ? instance.getDatasetMeta(datasetIndex) : null;
        if (!selection || datasetIndex < 0 || !meta || meta.hidden) {
            instance.$selectionGuide = null;
            instance.setActiveElements?.([]);
            instance.update("none");
            return;
        }
        const source = series.find((item) => item.chart.id === selection.seriesId);
        const row = source?.chart.tableData?.[selection.index];
        revealTimePoint(instance, chartTimePoint(row)?.x ?? NaN);
        const displayIndex = instance.data.datasets[datasetIndex]?.data?.findIndex(
            (point) => point.sourceIndex === selection.index
        );
        if (!Number.isInteger(displayIndex) || displayIndex < 0) {
            return;
        }
        instance.$selectionGuide = {
            index: displayIndex,
            datasetIndex,
            color: source?.color || contrastColor(theme),
            contrast: contrastColor(theme),
        };
        instance.setActiveElements?.([{ datasetIndex, index: displayIndex }]);
        const point = instance.getDatasetMeta(datasetIndex)?.data?.[displayIndex];
        if (point && !point.skip && instance.tooltip) {
            instance.tooltip.setActiveElements(
                [{ datasetIndex, index: displayIndex }],
                { x: point.x, y: point.y }
            );
        }
        instance.update("none");
        onZoomChange(COMBINED_CHART_ID, instance, "api");
    }, [onZoomChange, selection, series, theme]);

    const selectedSource = selection
        ? series.find((item) => item.chart.id === selection.seriesId)
        : null;
    const selectedRow = selectedSource?.chart.tableData?.[selection.index] || null;

    return (
        <article className="graph-page__card graph-page__chart-card">
            <div className="graph-page__card-head graph-page__card-head--split">
                <div>
                    <h2>Combined chart</h2>
                    <p>{series.length} series on one time axis</p>
                </div>
            </div>
            <div className="graph-page__zoom">
                <button type="button" className="graph-page__button" onClick={() => { onManualZoom?.(); chartRef.current?.zoom?.({ x: 1.2, y: 1 }); }}>
                    Zoom In
                </button>
                <button type="button" className="graph-page__button" onClick={() => { onManualZoom?.(); chartRef.current?.zoom?.({ x: 0.8, y: 1 }); }}>
                    Zoom Out
                </button>
                <button type="button" className="graph-page__button" onClick={() => onResetZoom?.(chartRef.current)} disabled={!zoomed}>
                    Reset Zoom
                </button>
                <button type="button" className="graph-page__button" onClick={onClearCursor} disabled={!cursorActive}>
                    Clear Cursor
                </button>
                {zoomed && <span className="graph-page__zoom-state">Zoomed range</span>}
            </div>
            <p className="graph-page__hint">
                Each series keeps its own timestamps. Wheel zooms the time axis. Drag a range to zoom. Shift-drag pans. Click the legend to hide a series.
            </p>
            {selectedRow && (
                <p className="graph-page__selection" role="status">
                    Selected {selectedSource.label} · {formatLocalDateTime(selectedRow.timestamp)} · {selectedRow.value}
                    {selectedSource.chart.visible === false ? " · series hidden" : ""}
                </p>
            )}
            <div className="graph-page__plot">
                <Line ref={chartRef} datasetIdKey="seriesId" data={data} options={options} />
            </div>
        </article>
    );
};

export { TagTrend, CombinedTrend };

